import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import CodeSnippetBlock from "ds/components/CodeSnippetBlock";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { generateExampleTrustPolicyStatement } from "./constants";
import { GET_SPACELIFT_AWS_ACCOUNT_ID } from "./gql";

type AwsIntegrationTrustPolicyExampleProps = {
  tagAssumeRole: boolean;
  showWarning: boolean;
};

const AwsIntegrationTrustPolicyExample = ({
  tagAssumeRole,
  showWarning,
}: AwsIntegrationTrustPolicyExampleProps) => {
  const { accountName } = useTypedContext(AccountContext);
  const { data } = useQuery(GET_SPACELIFT_AWS_ACCOUNT_ID);

  const parsedAssumeRolePolicyStatement = useMemo(() => {
    if (data?.spaceliftAwsAccountId) {
      return JSON.stringify(
        generateExampleTrustPolicyStatement(accountName, data.spaceliftAwsAccountId, tagAssumeRole),
        null,
        2
      );
    }

    return undefined;
  }, [accountName, data, tagAssumeRole]);

  return (
    <Banner
      variant={showWarning ? "warning" : "info"}
      title="Configure the trust policy for the IAM role"
    >
      {showWarning && (
        <Typography tag="p" variant="p-body3" margin="large 0 0 0">
          <b>
            {tagAssumeRole
              ? "The IAM role's trust policy must be updated in AWS to include the new TagSession statement."
              : "The IAM role's trust policy must be updated in AWS to remove the TagSession statement."}
          </b>
        </Typography>
      )}

      <Typography tag="p" variant="p-body3" margin="large 0 0 0">
        Here's an example trust policy statement you can use, that allows any stack within your
        Spacelift account to use the IAM Role:
      </Typography>

      {parsedAssumeRolePolicyStatement && (
        <Box margin="small 0 0 0">
          <CodeSnippetBlock language="json" allowCopy>
            {parsedAssumeRolePolicyStatement}
          </CodeSnippetBlock>
        </Box>
      )}
    </Banner>
  );
};

export default AwsIntegrationTrustPolicyExample;
